/* eslint-disable no-await-in-loop */
import axios from 'axios';
import { Placeholder, ERROR_STATUS, ACK_STATUS } from '../constants/constants';
import * as Endpoints from '../constants/routes';

/**
 * Reads the x-csrf-token cookie value set by the server's CSRF middleware.
 * Used with the double-submit cookie pattern — the value is echoed back in
 * the request header so the server can verify the request originates from
 * our own JavaScript (an attacker's page cannot read this cookie).
 * @returns {string}
 */
const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)x-csrf-token=([^;]+)/);
  return match ? match[1] : '';
};

const twoMinutes = 2 * 60 * 1000;

/**
 * @name getAcknowledgementUpdates
 * @param {String} transactionId
 * @param {Date | undefined} requestTimestamp
 * @returns {String | Object}
 */
export const getAcknowledgementUpdates = async (transactionId = '') => {
  if (!transactionId) return {};
  const endpoint = Endpoints.QR_ACK_ENDPOINT.replace(Placeholder, transactionId);
  return fetchAcknowledgementResponseData(endpoint, twoMinutes);
};

export const fetchAcknowledgementResponseData = async (endpoint, timeout = twoMinutes) => {
  if (timeout > 0) {
    const requestStartTime = Date.now();
    try {
      const response = await axios.get(endpoint, { timeout: twoMinutes });

      // If pending and not 2 minutes then fetch again
      if (response?.data?.status === ACK_STATUS.PENDING) {
        const requestEndTime = Date.now();
        const timeTakenToTimeout = requestEndTime - requestStartTime;
        return fetchAcknowledgementResponseData(endpoint, timeout - timeTakenToTimeout);
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === ERROR_STATUS.Request_Timeout) {
        const requestEndTime = Date.now();
        const timeTakenToTimeout = requestEndTime - requestStartTime;
        return fetchAcknowledgementResponseData(endpoint, timeout - timeTakenToTimeout);
      }
    }
  }

  throw new Error('Acknowledgment request timedout');
};

/**
 * @name triggerHelpNeededNotification
 * @param {String} qrId
 * @param @optional {Object} latlng
 * @returns {Object} {redirectionUri, deviceId, transactionId}
 */
export const triggerHelpNeededNotification = async (qrId) => {
  if (!qrId) throw new Error('Invalid qrId');
  const endpoint = Endpoints.TRIGGER_NOTIFICATION.replace(Placeholder, qrId);
  const response = await axios.post(endpoint, {}, { headers: { 'x-csrf-token': getCsrfToken() } });
  return response.data;
};

/**
 * @name submitFeedback
 * @param {String} qrId
 * @param {String} transactionId
 * @param {Number} rating
 * @param {Array} cannedResponses
 * @param {String} feedackText
 * @param {Any} metFlag
 */
export const submitFeedback = (qrId, transactionId, rating, eventTimestamp, cannedResponses = [], feedbackText = '', metFlag = null) => {
  const endpoint = Endpoints.SUBMIT_FEEDBACK;
  const payload = {
    deviceId: qrId,
    rating,
    feedback: feedbackText,
    transactionId,
    cannedResponses,
    eventTimestamp,
  };
  // Only add metFlag to payload if it's defined (for isCA true)
  if (metFlag !== null && metFlag !== undefined) {
    payload.metFlag = metFlag;
  }

  axios.post(endpoint, payload, { headers: { 'x-csrf-token': getCsrfToken() } });
};
